import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, Plus, Globe2, X as XIcon, Check } from "lucide-react";
import { getAdminAuthHeaders } from "./AdminAuthContext";
import { translateAdminError } from "./adminErrors";
import { StatusBadge } from "./StatusBadge";
import { AdminPagination } from "./AdminPagination";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COUNTRY_NAMES_ES = { ES: "España", UY: "Uruguay", US: "Estados Unidos", AR: "Argentina", BR: "Brasil" };
const COUNTRY_CODES = Object.keys(COUNTRY_NAMES_ES);

const EMPTY_FORM = { name: "", owner_email: "", website: "", industry: "", size: "", country_code: "", city: "", location: "", description: "" };

export default function AdminCompaniesPage() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [accessFilter, setAccessFilter] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const limit = 15;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/companies`, {
        headers: getAdminAuthHeaders(),
        params: { keyword: keyword || undefined, status: statusFilter || undefined, country_code: countryFilter || undefined, page, limit },
      });
      setCompanies(res.data.companies);
      setTotal(res.data.total);
    } catch (err) {
      toast.error(translateAdminError(err?.response?.data?.detail) || "No se pudieron cargar las empresas");
      setCompanies([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [keyword, statusFilter, countryFilter, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [keyword, statusFilter, countryFilter, accessFilter]);

  // El plan/acceso todavía filtra client-side (no hay volumen que lo justifique en el
  // backend por ahora); orden también es client-side sobre la página cargada.
  const visibleCompanies = companies
    .filter((c) => {
      if (accessFilter && (c.access_type || "none") !== accessFilter) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === "name") return (a.name || "").localeCompare(b.name || "");
      if (sortBy === "jobs") return (b.job_count || 0) - (a.job_count || 0);
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });

  const toggleStatus = async (c) => {
    setActionLoading(c.company_id);
    try {
      const action = c.status === "suspended" ? "activate" : "suspend";
      await axios.post(`${API}/admin/companies/${c.company_id}/${action}`, {}, { headers: getAdminAuthHeaders() });
      toast.success(action === "activate" ? `${c.name} reactivada` : `${c.name} suspendida`);
      await load();
    } catch (err) {
      toast.error(translateAdminError(err?.response?.data?.detail));
    } finally {
      setActionLoading("");
    }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!createForm.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    const email = createForm.owner_email.trim();
    if (!email) { toast.error("El email del propietario es obligatorio"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error("Ingresá un email válido para el propietario"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/companies`, createForm, { headers: getAdminAuthHeaders() });
      toast.success(`Empresa "${createForm.name}" creada`);
      setCreateOpen(false);
      setCreateForm(EMPTY_FORM);
      await load();
    } catch (err) {
      toast.error(translateAdminError(err?.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div data-testid="admin-companies-page">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <h1 className="text-xl font-bold text-slate-900 mb-1">Empresas</h1>
          <p className="text-sm text-slate-500">Empresas registradas y su plan / acceso a la plataforma</p>
        </div>
        <Button data-testid="admin-companies-create-button" onClick={() => setCreateOpen(true)} className="bg-[#028CF8] hover:bg-[#0270C5] shrink-0">
          <Plus className="h-4 w-4 mr-1.5" /> Crear empresa
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 mt-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input data-testid="admin-companies-search-input" value={keyword} onChange={(e) => setKeyword(e.target.value)}
            placeholder="Buscar por nombre de empresa..." className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-sm" />
        </div>
        <select data-testid="admin-companies-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Todos los estados</option>
          <option value="active">Activo</option>
          <option value="suspended">Suspendido</option>
          <option value="archived">Archivado</option>
        </select>
        <select data-testid="admin-companies-country-filter" value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Todos los países</option>
          {COUNTRY_CODES.map((cc) => <option key={cc} value={cc}>{COUNTRY_NAMES_ES[cc]}</option>)}
        </select>
        <select data-testid="admin-companies-access-filter" value={accessFilter} onChange={(e) => setAccessFilter(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Todos los planes</option>
          <option value="trial">Trial</option>
          <option value="manual">Acceso manual</option>
          <option value="none">Sin plan asignado</option>
        </select>
        <select data-testid="admin-companies-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="recent">Ordenar: Fecha de alta</option>
          <option value="name">Ordenar: Nombre (A-Z)</option>
          <option value="jobs"># Empleos (mayor a menor)</option>
        </select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[900px] table-fixed" data-testid="admin-companies-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3 w-[180px]">Nombre</th>
              <th className="text-left px-4 py-3 w-[170px]">Ubicación / País</th>
              <th className="text-left px-4 py-3 w-[150px]">Plan / Acceso</th>
              <th className="text-left px-4 py-3 w-[100px]">Estado</th>
              <th className="text-left px-4 py-3 w-[80px]"># Empleos</th>
              <th className="text-left px-4 py-3 w-[210px]">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {visibleCompanies.map((c) => (
              <tr key={c.company_id} data-testid={`admin-company-row-${c.company_id}`} className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors">
                <td className="px-4 py-3 font-medium text-slate-900 truncate" title={c.name}>{c.name}</td>
                <td className="px-4 py-3 text-slate-600 truncate">
                  <div className="flex items-center gap-1.5">
                    <Globe2 className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <span className="truncate">{c.location || "—"}{c.country_code ? ` · ${COUNTRY_NAMES_ES[c.country_code] || c.country_code}` : ""}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col items-start gap-1">
                    <StatusBadge value={c.access_type} testId={`admin-company-access-${c.company_id}`} />
                    {c.access_end && c.access_type !== "none" && (
                      <span data-testid={`admin-company-access-end-${c.company_id}`} className="text-xs text-slate-500">
                        hasta {new Date(c.access_end).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3"><StatusBadge value={c.status} testId={`admin-company-status-${c.company_id}`} /></td>
                <td className="px-4 py-3 text-slate-600">{c.job_count ?? 0}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-3 flex-wrap">
                    <button data-testid={`admin-company-view-${c.company_id}`} onClick={() => navigate(`/admin/companies/${c.company_id}`)} className="text-xs font-semibold text-[#028CF8] hover:underline">Ver / Editar</button>
                    {c.status !== "archived" && (
                      <button
                        data-testid={`admin-company-toggle-status-${c.company_id}`}
                        onClick={() => toggleStatus(c)}
                        disabled={actionLoading === c.company_id}
                        className={`text-xs font-semibold hover:underline ${c.status === "suspended" ? "text-emerald-600" : "text-amber-600"}`}
                      >
                        {actionLoading === c.company_id ? "..." : c.status === "suspended" ? "Reactivar" : "Suspender"}
                      </button>
                    )}
                    {c.status !== "archived" && (
                      <button data-testid={`admin-company-archive-${c.company_id}`} onClick={() => navigate(`/admin/companies/${c.company_id}?archive=1`)} className="text-xs font-semibold text-red-600 hover:underline">
                        Archivar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!loading && visibleCompanies.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-slate-500 text-sm">Sin resultados</td></tr>
            )}
          </tbody>
        </table>
        {loading && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 text-[#028CF8] animate-spin" /></div>}
      </div>

      <AdminPagination page={page} total={total} limit={limit} onPageChange={setPage} testidPrefix="admin-companies" />

      {/* ── Crear empresa — drawer ancho ── */}
      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent data-testid="admin-company-create-sheet" className="w-full sm:max-w-[520px] md:max-w-[640px] p-0 flex flex-col">
          <form onSubmit={submitCreate} className="flex flex-col h-full">
            <SheetHeader className="p-6 pb-4 border-b border-slate-100 shrink-0">
              <SheetTitle className="font-poppins text-xl">Crear empresa</SheetTitle>
              <p className="text-sm text-slate-500">Alta manual de una empresa en la plataforma</p>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Nombre *</label>
                <Input data-testid="admin-company-create-name" required value={createForm.name} onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Email del propietario (cuenta empresa existente) *</label>
                <Input data-testid="admin-company-create-owner-email" type="text" required value={createForm.owner_email} onChange={(e) => setCreateForm((f) => ({ ...f, owner_email: e.target.value }))} placeholder="propietario@empresa.com" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Sitio web</label>
                <Input data-testid="admin-company-create-website" value={createForm.website} onChange={(e) => setCreateForm((f) => ({ ...f, website: e.target.value }))} placeholder="https://" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500 mb-1 block">Industria</label>
                  <Input data-testid="admin-company-create-industry" value={createForm.industry} onChange={(e) => setCreateForm((f) => ({ ...f, industry: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 mb-1 block">Tamaño del equipo</label>
                  <Input data-testid="admin-company-create-size" value={createForm.size} onChange={(e) => setCreateForm((f) => ({ ...f, size: e.target.value }))} placeholder="Ej: 11-50" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500 mb-1 block">País</label>
                  <select data-testid="admin-company-create-country" value={createForm.country_code} onChange={(e) => setCreateForm((f) => ({ ...f, country_code: e.target.value }))} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    <option value="">Seleccioná un país</option>
                    {COUNTRY_CODES.map((cc) => <option key={cc} value={cc}>{COUNTRY_NAMES_ES[cc]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 mb-1 block">Ciudad</label>
                  <Input data-testid="admin-company-create-city" value={createForm.city} onChange={(e) => setCreateForm((f) => ({ ...f, city: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Ubicación visible</label>
                <Input data-testid="admin-company-create-location" value={createForm.location} onChange={(e) => setCreateForm((f) => ({ ...f, location: e.target.value }))} placeholder="Ej: Montevideo, Uruguay" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Descripción</label>
                <Textarea data-testid="admin-company-create-description" value={createForm.description} onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))} className="min-h-[80px]" />
              </div>
            </div>
            <SheetFooter className="p-4 border-t border-slate-100 shrink-0 gap-2">
              <Button type="button" data-testid="admin-company-create-cancel" variant="outline" onClick={() => { setCreateOpen(false); setCreateForm(EMPTY_FORM); }}>
                <XIcon className="h-4 w-4 mr-1.5" /> Cancelar
              </Button>
              <Button type="submit" data-testid="admin-company-create-submit" disabled={creating} className="bg-[#028CF8] hover:bg-[#0270C5]">
                {creating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />} Crear empresa
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
